"use client";

import { TradeInOption } from "@alba-cars/common-modules";
import parsePhoneNumberFromString, { isValidPhoneNumber } from "libphonenumber-js";
import { z } from "zod";

// Define FirstStepSchema
export const FirstStepSchema = z.object({
  // Contact Details
  name: z.string().trim().nonempty({ message: "Name is required" }),
  phone: z
    .string()
    .min(8, "Phone number is required")
    .refine((value) => {
      if (!value) return false;
      const phoneNumber = parsePhoneNumberFromString(value);
      if (!phoneNumber) return false;
      return isValidPhoneNumber(value);
    }, "Please enter a valid phone number"),
});

export type FirstStepSchemaType = z.infer<typeof FirstStepSchema>;

// Define SecondStepSchema without `transform`
export const SecondStepSchema = z.object({
  // Brand fields
  brand: z.string({ message: "Make is required" }).trim().nonempty({ message: "Make is required" }),
  brandName: z.string().nonempty({ message: "Make name is required" }),

  // Model fields
  model: z
    .union([z.string().trim(), z.null()])
    .transform((val) => val || "")
    .pipe(z.string().nonempty({ message: "Model is required" })),

  modelName: z.string().nonempty({ message: "Model name is required" }),

  // Model Version fields - optional
  modelVersion: z
    .union([z.string().trim(), z.null()])
    .transform((val) => val || "")
    .optional(),
  modelVersionName: z
    .union([z.string(), z.null()])
    .transform((val) => val || "")
    .optional(),

  specification: z.string().trim().nonempty({ message: "Specification is required" }),
  specificationName: z.string().nonempty({ message: "Specification name is required" }),

  // Car Details
  year: z.string().trim().nonempty({ message: "Year is required" }),
  mileage: z
    .union([z.string().transform((val) => (val === "" ? 0 : Number(val))), z.number()])
    .refine((val) => val >= 0, {
      message: "Mileage must be a positive number",
    }),
  // expectedSellingPrice: z.union([
  //   z.string().transform((val) => (val === "" ? 0 : Number(val))),
  //   z.number()
  // ]).refine((val) => val > 0, {
  //   message: "Expected selling price must be greater than 0",
  // }),
  tradeIn: z.nativeEnum(TradeInOption, {
    errorMap: () => ({ message: "Please select a valid trade-in option" }),
  }),

  description: z.string().trim().optional(),
});

export const ThirdStepSchema = z.object({
  images: z.array(z.any()).optional(),
});

export const SellCarFormSchema = FirstStepSchema.merge(SecondStepSchema)
  .merge(ThirdStepSchema)
  .transform((data) => ({
    name: data.name,
    phone: data.phone,

    brand: data.brand,
    brandName: data.brandName,

    model: data.model,
    modelName: data.modelName,

    modelVersion: data.modelVersion,
    modelVersionName: data.modelVersionName,

    specification: data.specification,
    specificationName: data.specificationName,

    year: data.year,
    mileage: Number(data.mileage),
    // expectedSellingPrice: Number(data.expectedSellingPrice),

    tradeIn: data.tradeIn,
    description: data.description,
    images: (data.images?.filter((image) => image?.url)?.map((image: any) => image.url) ?? []) as string[],
  }));

export type SecondStepSchemaType = z.infer<typeof SecondStepSchema>;
export type SellCarFormSchemaType = z.infer<typeof SellCarFormSchema>;
